import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createChecklistItem, updateChecklistItem } from "@/modules/checklist/api";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { RichTextEditor } from "@/shared/ui/rich-text-editor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { Badge } from "@/shared/ui/badge";
import { Checkbox } from "@/shared/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Activity, Languages, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PRIMARY_LANGUAGE } from "@/shared/config/languages";
import { useLanguage } from "@/core/LanguageContext";
import type { Json } from "@/integrations/supabase/types";
import { cn } from "@/shared/lib/utils";
import type { ChecklistItem, ChecklistInfo, FieldMapping, ItemType, AssignmentOption } from "./types";
import { parseAssignment, buildAssignment } from "./types";

interface ItemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklist: ChecklistInfo;
  editingItem: ChecklistItem | null;
  fieldMappings: FieldMapping[];
  assignmentOptions: AssignmentOption[];
  showAssignmentSelector: boolean;
  nextSortOrder: number;
}

export function ItemFormDialog({
  open,
  onOpenChange,
  checklist,
  editingItem,
  fieldMappings,
  assignmentOptions,
  showAssignmentSelector,
  nextSortOrder,
}: ItemFormDialogProps) {
  const queryClient = useQueryClient();
  const { availableLanguages } = useLanguage();

  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [labelTranslations, setLabelTranslations] = useState<Record<string, string>>({});
  const [descriptionTranslations, setDescriptionTranslations] = useState<Record<string, string>>({});
  const [itemType, setItemType] = useState<ItemType>("yes_no");
  const [isRequired, setIsRequired] = useState(true);
  const [isInspection, setIsInspection] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState("");
  const [itemAssignment, setItemAssignment] = useState("none");
  const [selectedFieldMappingIds, setSelectedFieldMappingIds] = useState<string[]>([]);
  const [previewValues, setPreviewValues] = useState<Map<string, { value: string; loading: boolean }>>(new Map());
  const [isTranslating, setIsTranslating] = useState<'label' | 'description' | null>(null);

  // Populate form when dialog opens
  useEffect(() => {
    if (!open) return;
    if (editingItem) {
      setLabel(editingItem.label);
      setDescription(editingItem.description || "");
      setLabelTranslations(editingItem.label_translations || {});
      setDescriptionTranslations(editingItem.description_translations || {});
      setItemType(editingItem.item_type);
      setIsRequired(editingItem.is_required);
      setIsInspection(editingItem.is_inspection);
      setDropdownOptions(editingItem.dropdown_options?.join("\n") || "");
      setItemAssignment(buildAssignment(editingItem));
      const existingMappingIds = editingItem.field_mappings?.length > 0
        ? [...editingItem.field_mappings].sort((a, b) => a.display_order - b.display_order).map(m => m.field_mapping_id)
        : editingItem.field_mapping_id ? [editingItem.field_mapping_id] : [];
      setSelectedFieldMappingIds(existingMappingIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingItem?.id]);

  const resetForm = () => {
    setLabel("");
    setDescription("");
    setLabelTranslations({});
    setDescriptionTranslations({});
    setItemType("yes_no");
    setIsRequired(true);
    setIsInspection(false);
    setDropdownOptions("");
    setItemAssignment("none");
    setSelectedFieldMappingIds([]);
    setPreviewValues(new Map());
    setIsTranslating(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // --- Preview values for field lookups ---

  const fetchPreviewValues = async () => {
    if (selectedFieldMappingIds.length === 0) {
      setPreviewValues(new Map());
      return;
    }
    setPreviewValues(prev => {
      const next = new Map(prev);
      selectedFieldMappingIds.forEach(id => {
        next.set(id, { value: next.get(id)?.value ?? '\u2014', loading: true });
      });
      return next;
    });
    try {
      const { data, error } = await supabase.functions.invoke("influxdb-query", {
        body: {
          factory_id: checklist.factory_id,
          mapping_ids: selectedFieldMappingIds,
        },
      });
      if (error) throw error;
      const results = data?.data || {};
      setPreviewValues(() => {
        const next = new Map<string, { value: string; loading: boolean }>();
        selectedFieldMappingIds.forEach(id => {
          const result = results[id];
          next.set(id, {
            value: result?.value !== undefined && result?.value !== null ? String(result.value) : '\u2014',
            loading: false,
          });
        });
        return next;
      });
    } catch {
      setPreviewValues(prev => {
        const next = new Map(prev);
        selectedFieldMappingIds.forEach(id => {
          next.set(id, { value: 'Error', loading: false });
        });
        return next;
      });
    }
  };

  useEffect(() => {
    if (selectedFieldMappingIds.length === 0 || itemType !== 'field_lookup') {
      setPreviewValues(new Map());
      return;
    }
    const timeout = setTimeout(() => {
      fetchPreviewValues();
    }, 600);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldMappingIds, itemType]);

  // --- AI Translation ---

  const translateContent = async (text: string, sourceLanguage: string, targetLanguage: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('translate-content', {
      body: { text, sourceLanguage, targetLanguage }
    });
    if (error) throw error;
    return data.translatedText;
  };

  const handleTranslateLabel = async (fromLang: string, toLang: string) => {
    setIsTranslating('label');
    try {
      const sourceText = fromLang === PRIMARY_LANGUAGE ? label : (labelTranslations[fromLang] || '');
      if (!sourceText.trim()) {
        toast.error(`No ${fromLang === PRIMARY_LANGUAGE ? 'English' : 'secondary language'} label to translate`);
        setIsTranslating(null);
        return;
      }
      const translated = await translateContent(sourceText, fromLang, toLang);
      if (toLang === PRIMARY_LANGUAGE) {
        setLabel(translated);
      } else {
        setLabelTranslations(prev => ({ ...prev, [toLang]: translated }));
      }
      toast.success("Label translated");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Translation failed: " + message);
    } finally {
      setIsTranslating(null);
    }
  };

  const handleTranslateDescription = async (fromLang: string, toLang: string) => {
    setIsTranslating('description');
    try {
      const sourceText = fromLang === PRIMARY_LANGUAGE ? description : (descriptionTranslations[fromLang] || '');
      if (!sourceText.trim()) {
        toast.error(`No ${fromLang === PRIMARY_LANGUAGE ? 'English' : 'secondary language'} description to translate`);
        setIsTranslating(null);
        return;
      }
      const translated = await translateContent(sourceText, fromLang, toLang);
      if (toLang === PRIMARY_LANGUAGE) {
        setDescription(translated);
      } else {
        setDescriptionTranslations(prev => ({ ...prev, [toLang]: translated }));
      }
      toast.success("Description translated");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error("Translation failed: " + message);
    } finally {
      setIsTranslating(null);
    }
  };

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: async () => {
      const assignment = parseAssignment(itemAssignment);
      const options = itemType === "dropdown"
        ? dropdownOptions.split("\n").map(o => o.trim()).filter(Boolean)
        : null;

      await createChecklistItem({
        checklist_id: checklist.id,
        tenant_id: checklist.tenant_id,
        label,
        description: description || null,
        item_type: isInspection ? itemType : 'yes_no',
        is_required: isRequired,
        is_inspection: isInspection,
        dropdown_options: isInspection ? options : null,
        sort_order: nextSortOrder,
        label_translations: labelTranslations as Json,
        description_translations: descriptionTranslations as Json,
        field_mapping_ids: isInspection && itemType === "field_lookup" ? selectedFieldMappingIds : [],
        ...assignment,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-items", checklist.id] });
      toast.success("Item added");
      handleClose();
    },
    onError: (error) => toast.error("Failed to add item: " + error.message),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingItem) throw new Error("No item to update");
      const assignment = parseAssignment(itemAssignment);
      const options = itemType === "dropdown"
        ? dropdownOptions.split("\n").map(o => o.trim()).filter(Boolean)
        : null;

      await updateChecklistItem({
        id: editingItem.id,
        tenant_id: checklist.tenant_id,
        label,
        description: description || null,
        item_type: isInspection ? itemType : 'yes_no',
        is_required: isRequired,
        is_inspection: isInspection,
        dropdown_options: isInspection ? options : null,
        label_translations: labelTranslations as Json,
        description_translations: descriptionTranslations as Json,
        field_mapping_ids: isInspection && itemType === "field_lookup" ? selectedFieldMappingIds : [],
        ...assignment,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-items", checklist.id] });
      toast.success("Item updated");
      handleClose();
    },
    onError: (error) => toast.error("Failed to update item: " + error.message),
  });

  // --- Submit ---

  const handleSubmit = () => {
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }

    if (isInspection && itemType === "dropdown") {
      const options = dropdownOptions.split("\n").map(o => o.trim()).filter(Boolean);
      if (options.length < 2) {
        toast.error("Dropdown needs at least 2 options");
        return;
      }
    }

    if (isInspection && itemType === "field_lookup" && selectedFieldMappingIds.length === 0) {
      toast.error("Please select at least one field mapping");
      return;
    }

    if (editingItem) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // --- Translation tab rendering helper ---

  const renderTranslationTab = (langCode: string, langName: string, otherLangCode: string | undefined) => {
    const isPrimary = langCode === PRIMARY_LANGUAGE;
    const isSecondaryTab = !isPrimary;

    return (
      <TabsContent key={langCode} value={langCode} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>
              Label {isPrimary ? "*" : ""}
              <span className="text-muted-foreground ml-1">({langName})</span>
            </Label>
            {otherLangCode && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => handleTranslateLabel(
                  isSecondaryTab ? PRIMARY_LANGUAGE : otherLangCode,
                  langCode,
                )}
                disabled={isTranslating === 'label'}
              >
                {isTranslating === 'label' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Languages className="w-3 h-3" />
                )}
                Import & Translate from {isSecondaryTab ? 'EN' : otherLangCode.toUpperCase()}
              </Button>
            )}
          </div>
          {isPrimary ? (
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Safety equipment checked?"
            />
          ) : (
            <Input
              value={labelTranslations[langCode] || ""}
              onChange={(e) => setLabelTranslations(prev => ({ ...prev, [langCode]: e.target.value }))}
              placeholder={`Translation for: ${label || "..."}`}
            />
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>
              Help Text
              <span className="text-muted-foreground ml-1">({langName})</span>
            </Label>
            {otherLangCode && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={() => handleTranslateDescription(
                  isSecondaryTab ? PRIMARY_LANGUAGE : otherLangCode,
                  langCode,
                )}
                disabled={isTranslating === 'description'}
              >
                {isTranslating === 'description' ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Languages className="w-3 h-3" />
                )}
                Import & Translate from {isSecondaryTab ? 'EN' : otherLangCode.toUpperCase()}
              </Button>
            )}
          </div>
          {isPrimary ? (
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Additional instructions for the operator"
              bucket="work-instruction-images"
            />
          ) : (
            <RichTextEditor
              value={descriptionTranslations[langCode] || ""}
              onChange={(val) => setDescriptionTranslations(prev => ({ ...prev, [langCode]: val }))}
              placeholder="Translation..."
              bucket="work-instruction-images"
            />
          )}
        </div>
      </TabsContent>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit Item" : "Add Check Item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {/* Label & description with optional translation tabs */}
          {availableLanguages.length > 1 ? (
            <Tabs defaultValue={PRIMARY_LANGUAGE} className="w-full">
              <TabsList className="w-full">
                {availableLanguages.map(lang => (
                  <TabsTrigger key={lang.code} value={lang.code} className="flex-1">
                    {lang.flag} {lang.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              {availableLanguages.map(lang => {
                const otherLang = availableLanguages.find(l => l.code !== lang.code);
                return renderTranslationTab(lang.code, lang.name, otherLang?.code);
              })}
            </Tabs>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Label *</Label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Safety equipment checked?"
                />
              </div>
              <div className="space-y-2">
                <Label>Help Text</Label>
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Additional instructions for the operator"
                  bucket="work-instruction-images"
                />
              </div>
            </div>
          )}

          {/* Inspection toggle */}
          <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="inspection" className="text-sm font-medium">Inspection Item</Label>
              <p className="text-xs text-muted-foreground">
                {isInspection
                  ? "Requires operator input and logs response"
                  : "Instruction only - logs execution time"}
              </p>
            </div>
            <Switch id="inspection" checked={isInspection} onCheckedChange={setIsInspection} />
          </div>

          {isInspection && (
            <>
              {/* Input type selector */}
              <div className="space-y-2">
                <Label>Input Type</Label>
                <Select value={itemType} onValueChange={(v) => setItemType(v as ItemType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes_no">Yes/No (Pass/Fail)</SelectItem>
                    <SelectItem value="text">Text Input</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="photo">Photo Upload</SelectItem>
                    <SelectItem value="dropdown">Dropdown Selection</SelectItem>
                    <SelectItem value="field_lookup">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Field Lookup (Real-time Value)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dropdown options */}
              {itemType === "dropdown" && (
                <div className="space-y-2">
                  <Label htmlFor="options">Options (one per line)</Label>
                  <Textarea
                    id="options"
                    value={dropdownOptions}
                    onChange={(e) => setDropdownOptions(e.target.value)}
                    placeholder={"Option 1\nOption 2\nOption 3"}
                    rows={4}
                  />
                </div>
              )}

              {/* Field lookup mappings */}
              {itemType === "field_lookup" && (
                <div className="space-y-3">
                  <Label>Select Data Fields</Label>
                  <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                    {fieldMappings.length === 0 ? (
                      <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                        No field mappings configured. Add mappings in UNS Mapper first.
                      </div>
                    ) : (
                      fieldMappings.filter(m => m.datapoint).map((mapping) => {
                        const isSelected = selectedFieldMappingIds.includes(mapping.id);
                        return (
                          <label
                            key={mapping.id}
                            className={cn(
                              "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors",
                              isSelected && "bg-primary/5"
                            )}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedFieldMappingIds(prev => [...prev, mapping.id]);
                                } else {
                                  setSelectedFieldMappingIds(prev => prev.filter(id => id !== mapping.id));
                                }
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm">{mapping.datapoint.display_name}</span>
                              <span className="text-xs text-muted-foreground block truncate">
                                {mapping.datapoint.bucket_name} / {mapping.datapoint.measurement_name} / {mapping.datapoint.field_name}
                                {mapping.datapoint.unit && ` (${mapping.datapoint.unit.symbol})`}
                              </span>
                            </div>
                            {isSelected && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                #{selectedFieldMappingIds.indexOf(mapping.id) + 1}
                              </Badge>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                  {selectedFieldMappingIds.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Live preview</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs gap-1"
                          onClick={fetchPreviewValues}
                        >
                          <RefreshCw className="w-3 h-3" />
                          Refresh
                        </Button>
                      </div>
                      <div className="grid gap-1">
                        {selectedFieldMappingIds.map(id => {
                          const mapping = fieldMappings.find(m => m.id === id);
                          const preview = previewValues.get(id);
                          if (!mapping?.datapoint) return null;
                          return (
                            <div key={id} className="flex items-center gap-2 px-2.5 py-1.5 bg-muted/40 rounded text-sm">
                              <Activity className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="text-muted-foreground truncate text-xs">{mapping.datapoint.display_name}</span>
                              <span className="ml-auto font-mono font-medium text-xs">
                                {preview?.loading ? (
                                  <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground" />
                                ) : (
                                  <>
                                    {preview?.value ?? '\u2014'}
                                    {mapping.datapoint.unit && (
                                      <span className="text-muted-foreground font-normal ml-0.5">{mapping.datapoint.unit.symbol}</span>
                                    )}
                                  </>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {selectedFieldMappingIds.length === 0
                      ? "Select one or more fields. Their values will be recorded when the instruction is completed."
                      : `${selectedFieldMappingIds.length} field${selectedFieldMappingIds.length !== 1 ? 's' : ''} selected. Values will be captured on submission.`
                    }
                  </p>
                </div>
              )}
            </>
          )}

          {/* Location assignment */}
          {showAssignmentSelector && (
            <div className="space-y-2">
              <Label>Assign to Location</Label>
              <Select value={itemAssignment} onValueChange={setItemAssignment}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {assignmentOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span style={{ paddingLeft: (opt.indent || 0) * 16 }}>
                        {opt.indent === 0 && opt.value !== "none" && "\uD83D\uDCCD "}
                        {opt.indent === 1 && "\u21B3 "}
                        {opt.indent === 2 && "\u21B3 "}
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optionally assign this item to a specific location within the checklist scope
              </p>
            </div>
          )}

          {/* Required toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="required">Required</Label>
            <Switch id="required" checked={isRequired} onCheckedChange={setIsRequired} />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingItem ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
